import { ArrowLeft, Bot, ExternalLink, GitBranch, RefreshCw, User } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FilesTab } from '../components/files/FilesTab';
import { FindingsTab } from '../components/findings/FindingsTab';
import { OverviewTab } from '../components/review/OverviewTab';
import { ReviewProgress } from '../components/review/ReviewProgress';
import { Badge, DemoBadge, StatusBadge } from '../components/ui/Badges';
import { Button } from '../components/ui/Button';
import { ErrorState, Notice, Skeleton, SkeletonRows } from '../components/ui/Feedback';
import { TabPanel, Tabs } from '../components/ui/Tabs';
import { useToast } from '../components/ui/Toast';
import { useConfig, useFindings, useRetryReview, useReview } from '../hooks/queries';
import { useSetPageContext } from '../hooks/usePageContext';
import { formatDateTime, repoLabel } from '../lib/format';
import { ApiError } from '../services/api';
import type { Review } from '../types/api';

type TabId = 'overview' | 'findings' | 'files' | 'summary';

function AiSummary({ review }: { review: Review }) {
  return (
    <section className="card max-w-3xl p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Bot className="h-4 w-4 text-accent" aria-hidden />
        <h2 className="text-sm font-semibold">AI summary</h2>
        {review.source === 'demo' ? <DemoBadge /> : <Badge>Generated by AI Pilot</Badge>}
      </div>
      <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed">{review.summary || 'No summary was produced.'}</p>
      <p className="mt-5 border-t border-line pt-4 text-xs text-faint">
        {review.source === 'demo'
          ? 'Deterministic sample output for demo mode. It is not an AI Pilot analysis.'
          : 'Generated from the PR title, description and analyzed diffs only. It may be incomplete or wrong; it does not see code outside the diff.'}
      </p>
    </section>
  );
}

export function ReviewDetailPage() {
  const { id = '' } = useParams();
  const review = useReview(id);
  const r = review.data;
  const completed = r?.status === 'completed';
  const findings = useFindings(id, completed);
  const { data: config } = useConfig();
  const retry = useRetryReview(id);
  const toast = useToast();
  const [tab, setTab] = useState<TabId>('overview');
  useSetPageContext(r ? `${repoLabel(r)}#${r.prNumber}` : null);

  if (review.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (review.error || !r) {
    const notFound = review.error instanceof ApiError && review.error.status === 404;
    return (
      <ErrorState
        title={notFound ? 'Review not found' : 'Could not load this review'}
        error={notFound ? 'It may have been deleted by the data-retention policy, or it belongs to another workspace.' : review.error}
        onRetry={notFound ? undefined : () => void review.refetch()}
      />
    );
  }

  const tabs = [
    { id: 'overview' as const, label: 'Overview' },
    { id: 'findings' as const, label: 'Findings', count: completed ? r.findingTotal : undefined },
    { id: 'files' as const, label: 'Changed Files', count: r.files.length || r.changedFileCount },
    { id: 'summary' as const, label: 'AI Summary' },
  ];

  return (
    <div>
      <Link to="/app/history" className="mb-4 inline-flex items-center gap-1 text-xs text-muted hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" /> Review history
      </Link>

      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={r.status} />
          {r.source === 'demo' && <DemoBadge />}
          <Badge className="bg-raised capitalize text-muted ring-line">{r.prState}</Badge>
        </div>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-[1.75rem]">
          {r.prTitle} <span className="font-normal text-faint">#{r.prNumber}</span>
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted">
          <a href={`https://github.com/${r.owner}/${r.repo}`} target="_blank" rel="noreferrer noopener" className="font-mono hover:text-ink">
            {repoLabel(r)}
          </a>
          <span className="inline-flex items-center gap-1">
            <User className="h-3.5 w-3.5" aria-hidden /> {r.prAuthor}
          </span>
          <span className="inline-flex items-center gap-1 font-mono text-xs">
            <GitBranch className="h-3.5 w-3.5" aria-hidden /> {r.sourceBranch} → {r.targetBranch}
          </span>
          <span className="text-xs">
            {r.changedFileCount} files · <span className="text-add">+{r.additions}</span> <span className="text-del">−{r.deletions}</span>
          </span>
          <span className="text-xs">Reviewed {formatDateTime(r.createdAt)}</span>
          <a href={r.prUrl} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-xs text-accent hover:underline">
            View on GitHub <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </header>

      {(r.status === 'queued' || r.status === 'processing') && <ReviewProgress review={r} />}

      {r.status === 'failed' && (
        <Notice
          tone="danger"
          title="The review failed"
          action={
            <Button
              size="sm"
              loading={retry.isPending}
              icon={<RefreshCw className="h-3.5 w-3.5" />}
              onClick={() =>
                retry.mutate(undefined, {
                  onSuccess: () => toast('Review restarted.', 'info'),
                  onError: (e) => toast(e.message, 'error'),
                })
              }
            >
              Retry
            </Button>
          }
        >
          {r.error?.message ?? 'Unknown error.'}{' '}
          {r.error && <span className="font-mono text-2xs text-faint">({r.error.code})</span>}
        </Notice>
      )}

      {completed && (
        <div className="mt-2">
          <Tabs items={tabs} value={tab} onChange={setTab} label="Review sections" />
          <TabPanel id={tab}>
            {tab === 'overview' && <OverviewTab review={r} />}
            {tab === 'summary' && <AiSummary review={r} />}
            {(tab === 'findings' || tab === 'files') &&
              (findings.isLoading ? (
                <div className="card">
                  <SkeletonRows rows={4} />
                </div>
              ) : findings.error ? (
                <ErrorState error={findings.error} onRetry={() => void findings.refetch()} />
              ) : tab === 'findings' ? (
                <FindingsTab review={r} findings={findings.data ?? []} canPublish={!!config?.github.canPublish} />
              ) : (
                <FilesTab review={r} findings={findings.data ?? []} />
              ))}
          </TabPanel>
        </div>
      )}
    </div>
  );
}
